import PatternValuable from './base/pattern-valuable'

export default class Formula extends PatternValuable { }

Formula.BasicFormula = class BasicFormula extends Formula {
  constructor (pattern, type, getValueFromParams) {
    super(pattern, type)
    this.getValueFromParams = getValueFromParams
  }

  async getValue ({ character, raw, ui, context }) {
    const params = await this.deriveParams({ character, raw, ui, context })
    const rawParams = this.deriveRawParams(raw)
    return await this.getValueFromParams(params, { character, raw, ui, context, rawParams })
  }
}

Formula.ComplexFormula = class ComplexFormula extends Formula {
  constructor (pattern, type, meaning) {
    super(pattern, type)
    this.meaning = meaning
  }

  async getValue ({ character, raw, ui, context }) {
    context = Object.assign({}, context, await this.deriveParams({ character, raw, ui, context }))
    return character.getRawValue({ raw: this.meaning, ui, type: this.type, context })
  }
}

Formula.PRESETS = [
  // numbers
  new Formula.BasicFormula('<number:A>+<number:B>', 'number', async ({ A, B }) => A + B),
  new Formula.BasicFormula('<number:A>-<number:B>', 'number', async ({ A, B }) => A - B),
  new Formula.BasicFormula('<number:A>*<number:B>', 'number', async ({ A, B }) => A * B),
  new Formula.BasicFormula('<number:A>/<number:B>', 'number', async ({ A, B }) => A / B),
  new Formula.BasicFormula('max of <number:A> and <number:B>', 'number', async ({ A, B }) => Math.max(A,B)),
  new Formula.BasicFormula('min of <number:A> and <number:B>', 'number', async ({ A, B }) => Math.min(A,B)),

  // comparison
  new Formula.BasicFormula('<any:A><=<any:B>', 'boolean', async ({ A, B }) => A <= B),
  new Formula.BasicFormula('<any:A>>=<any:B>', 'boolean', async ({ A, B }) => A >= B),
  new Formula.BasicFormula('<any:A>!=<any:B>', 'boolean', async ({ A, B }) => A !== B),
  new Formula.BasicFormula('<any:A>=<any:B>', 'boolean', async ({ A, B }) => A === B),
  new Formula.BasicFormula('<any:A>><any:B>', 'boolean', async ({ A, B }) => A > B),
  new Formula.BasicFormula('<any:A><<any:B>', 'boolean', async ({ A, B }) => A < B),

  // booleans
  new Formula.BasicFormula('<boolean:A> or <boolean:B>', 'boolean', async ({ A, B }) => A || B),
  new Formula.BasicFormula('<boolean:A> and <boolean:B>', 'boolean', async ({ A, B }) => A && B),
  new Formula.BasicFormula('not <boolean:A>', 'boolean', async ({ A }) => !A),
  new Formula.BasicFormula('<boolean:condition>?<any:onTrue>:<any:onFalse>', 'any', async ({ condition, onTrue, onFalse }) => (condition ? onTrue : onFalse)),
  new Formula.BasicFormula('switch <number:value> {outcomes}', 'any',  async ({ value, outcomes }, { ui, character, context }) => {
    for (const rawOutcome of Object.keys(outcomes)) {
      const outcome = character.getValueFromType(rawOutcome, 'range') || character.getValueFromType(rawOutcome, 'number')
      if (outcome?.includes(value) || outcome === value) {
        return await character.getRawValue({ raw: String(outcomes[rawOutcome]), ui, context })
      }
    }
  }),

  // arrays
  new Formula.BasicFormula('<array:A> join <array:B>', 'array', async ({ A, B }) => [].concat(A).concat(B)),
  new Formula.BasicFormula('<array:A> cross <array:B>', 'array', async ({ A, B }) => [].concat(A).filter(x => !B.excludes(x))),
  new Formula.BasicFormula('array of <number=1> <any:item>', 'array', async ({ number, item }) => Array(number).fill(Object.assign({}, item))),
  new Formula.BasicFormula('<any:item> [not] in <array>', 'boolean', async ({ item, not, array }) => array.inlcludes(item) !== not),
  new Formula.BasicFormula('sum of <array>', 'number', async ({ array }) => array.reduce((sum, item) => sum + item, 0)),
  new Formula.BasicFormula('first in <array>', 'any', async ({ array }) => array[0]),
  new Formula.BasicFormula('first <text:itemName> in <array> where <text:filter>', 'any', async ({ array, itemName, filter }) => {
    for (const item of array) {
      const innerContext = Object.assign({}, context, { [itemName]: item })
      const passFilter = await character.get(filter, { ui, context: innerContext, type: 'boolean' })
      if (passFilter) {
        return await character.get(formula, { ui, context: innerContext })
      }
    }
  }),
  new Formula.BasicFormula('last in <array>', 'any', async ({ array }) => array[array.length - 1]),
  new Formula.BasicFormula('last <text:itemName> in <array> where <text:filter>', 'any', async ({ array, itemName, filter }) => {
    for (const item of [].concat(array).reverse()) {
      const innerContext = Object.assign({}, context, { [itemName]: item })
      const passFilter = await character.get(filter, { ui, context: innerContext, type: 'boolean' })
      if (passFilter) {
        return await character.get(formula, { ui, context: innerContext })
      }
    }
  }),
  new Formula.BasicFormula('<text:formula=itemName> for <%text:itemName%> in <array>', 'any', async ({ formula, itemName, array }, { character, ui, context }) => {
    const newArray = []
    for (const index in array) {
      const innerContext = Object.assign({}, context, { [itemName]: array[index] })
      newArray[index] = await character.get(formula, { ui, context: innerContext })
    }
    return newArray
  }),
  new Formula.BasicFormula('<text:formula=itemName> for <%text:itemName%> in <array> where <text:filter>', 'any', async ({ formula, filter, itemName, array }, { character, ui, context }) => {
    const newArray = []
    for (const item of array) {
      const innerContext = Object.assign({}, context, { [itemName]: item })
      const passFilter = await character.get(filter, { ui, context: innerContext, type: 'boolean' })
      if (passFilter) {
        const vlaue = await character.get(formula, { ui, context: innerContext })
        newArray.push(value)
      }
    }
    return newArray
  }),

  // texts
  new Formula.BasicFormula('trimmed <text>', 'text', async ({ text }) => text.trim()),
  new Formula.BasicFormula('part <range:filter> of <text>', 'text', async ({ filter = 0, text = undefined }) => text.substring(filter.start, filter.end)),
  new Formula.BasicFormula('<%text:source%> as <%text:type%>', 'any', async ({ source, type }, { character, ui, context }) => {
    const raw = await character.get(source, { ui, type: 'text', context })
    return await character.get(raw, { ui, type, context })
  }),
  new Formula.BasicFormula('<text:source> starts with <text:part>', 'boolean', async ({ source, part }) => source.startsWith(part)),
  new Formula.BasicFormula('<text:source> ends with <text:part>', 'boolean', async ({ source, part }) => source.endsWith(part)),
  new Formula.BasicFormula('<text:source> contains <text:part>', 'boolean', async ({ source, part }) => source.includes(part)),
  new Formula.BasicFormula('<text:before=>`<text:insert>`<text:after=>', 'boolean', async ({ insert, before, after }, { character, ui, context }) => {
    const insertedValue = await character.get(insert, { ui, type: 'text', context })
    return `${before}${insert}${after}`
  }),

  // generic objects ("any")
  new Formula.BasicFormula('<any:obj>.<%text:field%>', 'any', async ({ obj, field }) => obj[field]),
  new Formula.BasicFormula('{<%text:fields%>}', 'any', async ({ fields }, { character, ui, context }) => {
    const fieldNames = fields.split(',').map(field => field.trim())
    const result = { }
    for (const fieldName of fieldNames) {
      result[fieldName] = await character.get(fieldNames, { ui, context })
    }
    return resutls
  }),

  // dice rolls
  new Formula.BasicFormula('<%number:amount=1%>d<%number:dice%>', 'number', async ({ dice, amount }) => {
    if (isFinite(dice)) {
      return new Array(amount).fill(1).map(() => Math.ceil(Math.random() * dice)).reduce((sum, val) => sum + val, 0)
    }
  }),

  // UI
  new Formula.BasicFormula('<text:type=text> input <text=choose>', 'any', async ({ type, text }, { ui }) => await ui.input(text, type)),
  new Formula.BasicFormula('choice <number:count=1> from <array:options>', 'text', async ({ options, count, text }, { ui }) => await ui.choose(text, options, count)),

  // character details
  new Formula.BasicFormula('the <text::name>', 'any', ({ raw }, { character }) => character.history.last(event => event.name === name)),
  new Formula.BasicFormula('class', 'text', (_params, { character }) => character.playbook.name)
]
